import { createContext, useContext, useState, useEffect, useCallback } from "react";
import API from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      setError(null);
      const { data } = await API.get("/auth/me");
      setUser(data);
      localStorage.setItem("auth_user", JSON.stringify(data));
    } catch (err) {
      setUser(null);
      localStorage.removeItem("auth_user");
      localStorage.removeItem("auth_token");
      if (err.response?.status !== 401) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      setError(null);
      const { data } = await API.post("/auth/login", { email, password });
      setUser(data);
      localStorage.setItem("auth_user", JSON.stringify(data));
      if (data.token) {
        localStorage.setItem("auth_token", data.token);
      }
      return data;
    } catch (err) {
      setUser(null);
      localStorage.removeItem("auth_user");
      localStorage.removeItem("auth_token");
      const errorMsg = err.response?.data?.detail || err.message;
      setError(errorMsg);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await API.post("/auth/logout");
    } catch (err) {
      // Still logout even if the API call fails
    } finally {
      setUser(null);
      localStorage.removeItem("auth_user");
      localStorage.removeItem("auth_token");
      setError(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
